import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, Bus, Users, Clock, MapPin, Banknote, CheckCircle, 
  UserPlus, Ticket, ChevronRight, X, Plus, Minus
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorWalkIn = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [availableSeats, setAvailableSeats] = useState([]);
  const [allStops, setAllStops] = useState([]);
  
  // Booking state
  const [step, setStep] = useState(1); // 1: select trip, 2: select seats, 3: passenger details, 4: confirm
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [boardingCity, setBoardingCity] = useState('');
  const [alightingCity, setAlightingCity] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completedBooking, setCompletedBooking] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login');
      return;
    }
    fetchTrips();
  }, [navigate]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const response = await api.get('/bus/operators/trips/today');
      setTrips(response.data.trips || []);
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/bus/operator/apply');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load trips',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrip = async (trip) => {
    setSelectedTrip(trip);
    setBoardingCity(trip.route?.origin?.city || '');
    setAlightingCity(trip.route?.destination?.city || '');
    setStep(2);
    
    // Fetch available seats
    await fetchAvailableSeats(trip.id, trip.route?.origin?.city, trip.route?.destination?.city);
  };

  const fetchAvailableSeats = async (tripId, boarding, alighting) => {
    try {
      const params = new URLSearchParams();
      if (boarding) params.append('boarding_city', boarding);
      if (alighting) params.append('alighting_city', alighting);
      
      const response = await api.get(`/bus/operators/trips/${tripId}/available-seats?${params.toString()}`);
      setAvailableSeats(response.data.availableSeats || []);
      setAllStops(response.data.allStops || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load available seats',
        variant: 'destructive'
      });
    }
  };

  const handleDestinationChange = async (newAlighting) => {
    setAlightingCity(newAlighting);
    setSelectedSeats([]);
    await fetchAvailableSeats(selectedTrip.id, boardingCity, newAlighting);
  };

  const toggleSeatSelection = (seat) => {
    if (selectedSeats.find(s => s.seatId === seat.seatId)) {
      setSelectedSeats(prev => prev.filter(s => s.seatId !== seat.seatId));
    } else {
      setSelectedSeats(prev => [...prev, seat]);
    }
  };

  const handleProceedToPassengers = () => {
    if (selectedSeats.length === 0) {
      toast({
        title: 'No Seats Selected',
        description: 'Please select at least one seat',
        variant: 'destructive'
      });
      return;
    }
    
    // Initialize passenger details for each seat
    setPassengers(selectedSeats.map(seat => ({
      name: '',
      phone: '',
      seat: seat.seatId,
      seat_class: seat.seatClass,
      id_number: ''
    })));
    setStep(3);
  };

  const updatePassenger = (index, field, value) => {
    setPassengers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleProceedToConfirm = () => {
    // Validate all passengers have name and phone
    for (let i = 0; i < passengers.length; i++) {
      if (!passengers[i].name || !passengers[i].phone) {
        toast({
          title: 'Missing Details',
          description: `Please fill in name and phone for Passenger ${i + 1}`,
          variant: 'destructive'
        });
        return;
      }
    }
    setStep(4);
  };

  const calculateTotal = () => {
    return selectedSeats.reduce((sum, seat) => sum + (seat.price || 0), 0);
  };

  const handleConfirmBooking = async () => {
    setSubmitting(true);
    try {
      const response = await api.post('/bus/operators/bookings/walk-in', {
        trip_id: selectedTrip.id,
        passengers: passengers.map(p => ({ ...p, phone: p.phone ? `+260${p.phone.replace(/\D/g, '')}` : '' })),
        boarding_city: boardingCity,
        alighting_city: alightingCity
      });
      
      setCompletedBooking(response.data.booking);
      setStep(5);
      
      toast({
        title: 'Booking Created!',
        description: `Ticket: ${response.data.booking.ticketCode}`,
      });
    } catch (error) {
      toast({
        title: 'Booking Failed',
        description: error.response?.data?.detail || 'Failed to create booking',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetBooking = () => {
    setSelectedTrip(null);
    setSelectedSeats([]);
    setPassengers([]);
    setCompletedBooking(null);
    setStep(1);
    fetchTrips();
  };

  const getSeatClassColor = (seatClass) => {
    switch (seatClass) {
      case 'vip': return 'bg-yellow-100 border-yellow-400 text-yellow-700';
      case 'business': return 'bg-purple-100 border-purple-400 text-purple-700';
      default: return 'bg-blue-100 border-blue-400 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/bus/operator">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Walk-in Booking</h1>
              <p className="text-green-100">Create booking for passenger at station</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 -mt-4">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? '✓' : s}
              </div>
              {s < 4 && <div className={`w-12 h-1 ${step > s ? 'bg-green-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Trip */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Select Trip</h2>
            
            {trips.length === 0 ? (
              <Card className="p-8 text-center">
                <Bus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Trips Available</h3>
                <p className="text-gray-500">You don't have any scheduled trips for today</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {trips.map((trip) => (
                  <Card 
                    key={trip.id}
                    className="p-4 cursor-pointer hover:border-green-300 transition-all"
                    onClick={() => handleSelectTrip(trip)}
                    data-testid={`trip-${trip.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-bold">{trip.departureTime}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-lg font-bold">{trip.arrivalTime}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>{trip.route?.origin?.city} → {trip.route?.destination?.city}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-500">{trip.date}</span>
                          <span className="text-gray-500">{trip.bus?.plateNumber}</span>
                          <span className="text-green-600 font-medium">{trip.availableSeats} seats available</span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Seats */}
        {step === 2 && selectedTrip && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Select Seats</h2>
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>
            
            {/* Trip Summary */}
            <Card className="p-4 mb-4 bg-green-50 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedTrip.route?.origin?.city} → {selectedTrip.route?.destination?.city}</p>
                  <p className="text-sm text-gray-600">{selectedTrip.date} at {selectedTrip.departureTime}</p>
                </div>
                <span className="text-green-600 font-bold">{availableSeats.length} available</span>
              </div>
            </Card>

            {/* Destination Selector (for intermediate stops) */}
            {allStops.length > 2 && (
              <Card className="p-4 mb-4">
                <Label className="mb-2 block">Passenger's Destination</Label>
                <select
                  value={alightingCity}
                  onChange={(e) => handleDestinationChange(e.target.value)}
                  className="w-full p-2 border rounded-md"
                  data-testid="destination-select"
                >
                  {allStops.slice(1).map((stop, idx) => (
                    <option key={idx} value={stop}>
                      {stop} {idx === allStops.length - 2 ? '(Final)' : ''}
                    </option>
                  ))}
                </select>
              </Card>
            )}

            {/* Seat Grid */}
            <Card className="p-4 mb-4">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {availableSeats.map((seat) => (
                  <button
                    key={seat.seatId}
                    onClick={() => toggleSeatSelection(seat)}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${
                      selectedSeats.find(s => s.seatId === seat.seatId)
                        ? 'bg-green-500 border-green-600 text-white'
                        : getSeatClassColor(seat.seatClass)
                    }`}
                    data-testid={`seat-${seat.seatId}`}
                  >
                    <div className="font-bold">{seat.seatId}</div>
                    <div className="text-xs">K{seat.price}</div>
                  </button>
                ))}
              </div>
              
              {/* Legend */}
              <div className="flex gap-4 mt-4 text-sm">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-yellow-100 border border-yellow-400 rounded" />
                  <span>VIP</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-purple-100 border border-purple-400 rounded" />
                  <span>Business</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-blue-100 border border-blue-400 rounded" />
                  <span>Standard</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-green-500 rounded" />
                  <span>Selected</span>
                </div>
              </div>
            </Card>

            {/* Selected Seats Summary */}
            {selectedSeats.length > 0 && (
              <Card className="p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{selectedSeats.length} seat(s) selected</p>
                    <p className="text-sm text-gray-500">
                      {selectedSeats.map(s => s.seatId).join(', ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-2xl font-bold text-green-600">K{calculateTotal()}</p>
                  </div>
                </div>
              </Card>
            )}

            <Button 
              onClick={handleProceedToPassengers}
              disabled={selectedSeats.length === 0}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Continue to Passenger Details
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Passenger Details */}
        {step === 3 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Passenger Details</h2>
              <Button variant="outline" size="sm" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>

            <div className="space-y-4">
              {passengers.map((passenger, idx) => (
                <Card key={idx} className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserPlus className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Passenger {idx + 1}</span>
                    <span className="text-sm text-gray-500">- Seat {passenger.seat}</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={passenger.name}
                        onChange={(e) => updatePassenger(idx, 'name', e.target.value)}
                        placeholder="Enter full name"
                        data-testid={`passenger-name-${idx}`}
                      />
                    </div>
                    <div>
                      <Label>Phone Number *</Label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium">+260</span>
                        <Input
                          type="tel"
                          className="rounded-l-none"
                          value={passenger.phone}
                          onChange={(e) => updatePassenger(idx, 'phone', e.target.value.replace(/\D/g, '').slice(0, 9))}
                          placeholder="XXX XXX XXX"
                          maxLength={9}
                          data-testid={`passenger-phone-${idx}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>ID Number (Optional)</Label>
                      <Input
                        value={passenger.id_number}
                        onChange={(e) => updatePassenger(idx, 'id_number', e.target.value)}
                        placeholder="NRC or Passport"
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <Button 
              onClick={handleProceedToConfirm}
              className="w-full mt-4 bg-green-600 hover:bg-green-700"
            >
              Review Booking
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === 4 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Confirm Booking</h2>
              <Button variant="outline" size="sm" onClick={() => setStep(3)}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            </div>

            <Card className="p-4 mb-4">
              <h3 className="font-medium mb-3">Trip Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Route</span>
                  <span>{boardingCity} → {alightingCity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date & Time</span>
                  <span>{selectedTrip?.date} at {selectedTrip?.departureTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Seats</span>
                  <span>{selectedSeats.map(s => s.seatId).join(', ')}</span>
                </div>
              </div>
            </Card>

            <Card className="p-4 mb-4">
              <h3 className="font-medium mb-3">Passengers</h3>
              {passengers.map((p, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium">{p.name}</p>
                    <p className="text-sm text-gray-500">{p.phone} • Seat {p.seat}</p>
                  </div>
                  <span className="font-medium">K{selectedSeats[idx]?.price || 0}</span>
                </div>
              ))}
            </Card>

            <Card className="p-4 mb-4 bg-green-50 border-green-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600">Total Amount</p>
                  <p className="text-sm text-gray-500">Cash Payment</p>
                </div>
                <p className="text-3xl font-bold text-green-600">K{calculateTotal()}</p>
              </div>
            </Card>

            <Button 
              onClick={handleConfirmBooking}
              disabled={submitting}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="confirm-walkin-btn"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating Booking...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm & Print Ticket
                </>
              )}
            </Button>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && completedBooking && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Booking Created!</h2>
            <p className="text-gray-500 mb-6">Ticket has been generated</p>

            <Card className="p-6 mb-6 bg-green-50 border-green-200">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Ticket className="h-6 w-6 text-green-600" />
                <span className="text-2xl font-mono font-bold">{completedBooking.ticketCode}</span>
              </div>
              
              <div className="text-sm space-y-1">
                <p>{completedBooking.boarding?.city} → {completedBooking.alighting?.city}</p>
                <p>{completedBooking.trip?.date} at {completedBooking.trip?.departureTime}</p>
                <p>Seats: {completedBooking.passengers?.map(p => p.seat).join(', ')}</p>
                <p className="font-bold mt-2">Total: K{completedBooking.grandTotal}</p>
              </div>
            </Card>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={resetBooking}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Booking
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/bus/operator/bookings')}
              >
                View Bookings
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusOperatorWalkIn;
